/**
 * @fileoverview Capa de compatibilidad para Firebase Firestore
 * Proporciona un proxy que intercepta llamadas a métodos legacy y las redirige a la API modular v9
 * Esto permite que el código antiguo o minificado siga funcionando sin cambios
 */

import { 
  collection, doc, getDoc, getDocs, addDoc, updateDoc, 
  deleteDoc, query, where, limit, orderBy
} from 'firebase/firestore';

/**
 * Capa de compatibilidad para Firestore
 * Proporciona métodos legacy que internamente usan la API modular v9
 */
class FirestoreCompatLayer {
  constructor(firestoreInstance) {
    this.db = firestoreInstance;
    this.attachCompatMethods();
  }

  /**
   * Adjunta métodos de compatibilidad al objeto db
   * También hace monkey-patching a nivel global para interceptar errores
   */
  attachCompatMethods() {
    // Método legacy collection
    this.db.collection = (collectionPath) => {
      console.warn('[FirestoreCompat] Uso de método legacy db.collection, considere migrar a la API modular v9');
      
      const collectionRef = collection(this.db, collectionPath);
      
      // Añadir métodos de compatibilidad al objeto de colección
      collectionRef.doc = (docId) => {
        const docRef = doc(this.db, collectionPath, docId);
        
        // Añadir métodos de compatibilidad al documento
        docRef.get = async () => {
          const snapshot = await getDoc(docRef);
          
          // Convertir el resultado al formato anterior
          return {
            id: snapshot.id,
            exists: snapshot.exists(),
            data: () => snapshot.data(),
            ref: docRef
          };
        };
        
        docRef.update = (data) => updateDoc(docRef, data);
        docRef.delete = () => deleteDoc(docRef);
        docRef.set = (data, options) => {
          if (options && options.merge) {
            return updateDoc(docRef, data);
          }
          // Aquí faltaría implementar setDoc, pero no lo incluimos para mantener el ejemplo simple
        };
        
        return docRef;
      };
      
      // Añadir métodos de consulta
      collectionRef.where = (...args) => {
        const q = query(collectionRef, where(...args));
        return this.enhanceQuery(q, collectionPath);
      };
      
      collectionRef.orderBy = (...args) => {
        const q = query(collectionRef, orderBy(...args));
        return this.enhanceQuery(q, collectionPath);
      };
      
      collectionRef.limit = (limitCount) => {
        const q = query(collectionRef, limit(limitCount));
        return this.enhanceQuery(q, collectionPath);
      };
      
      collectionRef.get = async () => {
        const snapshot = await getDocs(collectionRef);
        
        // Convertir resultado al formato anterior
        return {
          docs: snapshot.docs.map(doc => ({
            id: doc.id,
            exists: doc.exists(),
            data: () => doc.data(),
            ref: doc.ref
          })),
          empty: snapshot.empty,
          size: snapshot.size
        };
      };
      
      collectionRef.add = (data) => addDoc(collectionRef, data);
      
      return collectionRef;
    };
  }
  
  /**
   * Mejora un objeto query con métodos de compatibilidad
   * @param {object} q - Objeto query de Firestore v9
   * @param {string} collectionPath - Ruta de la colección
   * @returns {object} - Query mejorada con métodos de compatibilidad
   */
  enhanceQuery(q, collectionPath) {
    // Añadir métodos de compatibilidad al objeto query
    q.get = async () => {
      const snapshot = await getDocs(q);
      
      // Convertir resultado al formato anterior
      return {
        docs: snapshot.docs.map(doc => ({
          id: doc.id,
          exists: doc.exists(),
          data: () => doc.data(),
          ref: doc.ref
        })),
        empty: snapshot.empty,
        size: snapshot.size
      };
    };
    
    // Encadenar consultas adicionales
    q.where = (...args) => {
      const newQuery = query(q, where(...args));
      return this.enhanceQuery(newQuery, collectionPath);
    };
    
    q.orderBy = (...args) => {
      const newQuery = query(q, orderBy(...args));
      return this.enhanceQuery(newQuery, collectionPath);
    };
    
    q.limit = (limitCount) => {
      const newQuery = query(q, limit(limitCount));
      return this.enhanceQuery(newQuery, collectionPath);
    };
    
    return q;
  }
}

// Exportamos la clase para su uso
export default FirestoreCompatLayer;
